/**
 * PromptPopover — lightweight portal popover for editing the AI generation prompt.
 * Anchors below the trigger element, flips up if insufficient space.
 * Borrows positioning logic from DropdownPicker.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import styles from './PromptPopover.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  prompt: string;
  onPromptChange: (p: string) => void;
  onReset: () => void;
  // Append helpers
  actorSummary: string;
  sceneDescription: string;
  hasScreenplay: boolean;
  // Auto context
  metadataPreview: string;
  showMetadataPreview: boolean;
  isEnhance: boolean;
}

interface PopoverPos {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
}

export function PromptPopover({
  open, onClose, anchorEl,
  prompt, onPromptChange, onReset,
  actorSummary, sceneDescription, hasScreenplay,
  metadataPreview, showMetadataPreview, isEnhance,
}: Props) {
  const { t } = useTranslation('aiWorkspace');
  const popoverRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<PopoverPos | null>(null);
  const [copied, setCopied] = useState(false);

  const updatePosition = useCallback(() => {
    if (!anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const flipUp = spaceBelow < 200 && spaceAbove > spaceBelow;
    const popWidth = Math.min(340, window.innerWidth - 32);

    setPos({
      left: Math.max(16, Math.min(rect.left, window.innerWidth - popWidth - 16)),
      width: popWidth,
      ...(flipUp
        ? { bottom: window.innerHeight - rect.top + 4, maxHeight: Math.min(400, spaceAbove) }
        : { top: rect.bottom + 4, maxHeight: Math.min(400, spaceBelow) }),
    });
  }, [anchorEl]);

  useEffect(() => {
    if (!open) { setPos(null); return; }
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  const handleAddActors = useCallback(() => {
    if (!actorSummary) return;
    const sep = prompt && !prompt.endsWith('\n') ? '\n' : '';
    onPromptChange(prompt + sep + actorSummary);
  }, [actorSummary, prompt, onPromptChange]);

  const handleAddScene = useCallback(() => {
    if (!sceneDescription) return;
    const sep = prompt && !prompt.endsWith('\n') ? '\n' : '';
    onPromptChange(prompt + sep + sceneDescription);
  }, [sceneDescription, prompt, onPromptChange]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(prompt).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [prompt]);

  if (!open || !pos) return null;

  return createPortal(
    <>
      <div className={styles.backdrop} onClick={onClose} />
      <div
        ref={popoverRef}
        className={styles.popover}
        style={{
          position: 'fixed',
          left: pos.left,
          width: pos.width,
          maxHeight: pos.maxHeight,
          overflowY: 'auto',
          ...(pos.top != null ? { top: pos.top } : {}),
          ...(pos.bottom != null ? { bottom: pos.bottom } : {}),
        }}
      >
        <textarea
          className={styles.textarea}
          value={prompt}
          onChange={e => onPromptChange(e.target.value)}
          placeholder={isEnhance ? 'Describe the image style...' : 'Describe the video...'}
          rows={4}
          autoFocus
        />

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.actionBtn}
            disabled={!actorSummary}
            onClick={handleAddActors}
          >
            + {t('addActorLabel', 'Actor')}
          </button>
          <button
            type="button"
            className={styles.actionBtn}
            disabled={!hasScreenplay || !sceneDescription}
            onClick={handleAddScene}
          >
            + {t('addSceneLabel', 'Scene')}
          </button>
          <button type="button" className={styles.actionBtn} onClick={onReset}>
            {t('resetPrompt', 'Reset')}
          </button>
          <span className={styles.actionBtnRight}>
            {copied ? (
              <span className={styles.copied}>{t('copied', 'Copied!')}</span>
            ) : (
              <button type="button" className={styles.actionBtn} onClick={handleCopy}>
                {t('copyPrompt', 'Copy')}
              </button>
            )}
          </span>
        </div>

        {showMetadataPreview && metadataPreview && (
          <div className={styles.metadataSection}>
            <div className={styles.metadataLabel}>
              {t('autoContextLabel', 'Each shot will also include:')}
            </div>
            <div className={styles.metadataText}>{metadataPreview}</div>
          </div>
        )}

        <button type="button" className={styles.doneBtn} onClick={onClose}>
          {t('done', 'Done')}
        </button>
      </div>
    </>,
    document.body,
  );
}
